import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useT } from "../i18n";
import { useBooking } from "../BookingContext";

const FONT_BODY = {
  fontFamily:
    '-apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro", "Helvetica Neue", Arial, sans-serif',
};
const BORDER = "#E5E7EB";

/**
 * Shared main site header (Apple-style, sticky, blurred).
 * Works on the landing page (anchor links) and on sub-pages
 * (links resolve to `/` + anchor so nav still works from `/privacy`, `/areas-we-serve`, etc.).
 */
export default function SiteHeader() {
  const { lang, setLang, t } = useT();
  const { open: openBooking } = useBooking();
  const { pathname } = useLocation();
  const onHome = pathname === "/";

  const navItems = [
    { key: "services",   label: t("pm.nav.services") },
    { key: "technology", label: t("pm.nav.technology") },
    { key: "compare",    label: t("pm.nav.compare") },
    { key: "process",    label: t("pm.nav.process") },
  ];

  const hrefFor = (k) => (onHome ? `#${k}` : `/#${k}`);
  const logoHref = onHome ? "#top" : "/";

  return (
    <header
      data-testid="site-header"
      className="border-b"
      style={{
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
        background: "rgba(255,255,255,0.72)",
        borderColor: `${BORDER}66`,
      }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-2.5 flex items-center justify-between">
        {onHome ? (
          <a href={logoHref} className="flex items-center" aria-label="PropHero">
            <img src="/prophero-logo.png" alt="PropHero" className="h-5 w-auto" />
          </a>
        ) : (
          <Link to={logoHref} className="flex items-center" aria-label="PropHero">
            <img src="/prophero-logo.png" alt="PropHero" className="h-5 w-auto" />
          </Link>
        )}

        <nav className="hidden md:flex items-center gap-8">
          {navItems.map((n) => (
            <a
              key={n.key}
              href={hrefFor(n.key)}
              className="text-[12px] font-normal text-[#1D1D1F]/85 hover:text-[#0A0A0A] transition-colors"
              style={{ ...FONT_BODY, letterSpacing: "-0.01em" }}
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setLang(lang === "en" ? "es" : "en")}
            data-testid="site-header-lang-toggle"
            aria-label={lang === "en" ? "Switch to Spanish" : "Switch to English"}
            className="inline-flex items-center justify-center min-w-[36px] px-2.5 py-1 rounded-full text-[11px] font-normal text-[#515154] border transition-colors hover:text-[#0A0A0A]"
            style={{ borderColor: BORDER, ...FONT_BODY, letterSpacing: "0.02em" }}
          >
            {lang === "en" ? "EN" : "ES"}
          </button>
          <button
            onClick={() => openBooking("site_header_cta", "Schedule Consultation")}
            data-testid="site-header-cta"
            className="inline-flex items-center bg-[#023CFA] hover:bg-[#1E52FF] text-white px-3.5 py-1.5 rounded-full text-[12px] font-normal transition-colors"
            style={{ ...FONT_BODY, letterSpacing: "-0.01em" }}
          >
            {t("pm.cta.schedule")}
          </button>
        </div>
      </div>
    </header>
  );
}
